import ApexCharts, {ApexOptions} from 'apexcharts'
import React, {useEffect, useId, useMemo, useRef} from 'react'
import {FormattedNumber} from 'react-intl'
import {getCSS, getCSSVariableValue} from '../../../../_metronic/assets/ts/_utils'
import {useThemeMode} from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import clsx from 'clsx'
import LoadingChart from '@/app/modules/apps/dashboard/components/LoadingChart'

type Props = {
  className: string
  color: string
  secondColor?: string
  title?: string
  subtitle?: string
  series?: any
  categories?: any
  height?: string
  width?: string
  total?: number
  isCurrency?: boolean
  isTotal?: boolean
  data?: any[]
  headers?: any[]
  formatCurrency?: any[]
  layout?: 'vertical' | 'horizontal'
  isLoading?: boolean
  showTable?: boolean
}

/** Rounds axis/tooltip values so floating-point noise (e.g. 1.2000000000000002) never appears as labels */
function formatChartAxisValue(val: unknown, isCurrency: boolean): string {
  const n = typeof val === 'number' ? val : parseFloat(String(val))
  if (!Number.isFinite(n)) return isCurrency ? 'Rp. 0' : '0'
  if (isCurrency) {
    const rounded = Math.abs(n) >= 100 ? Math.round(n) : Math.round(n * 100) / 100
    return (
      'Rp. ' +
      new Intl.NumberFormat('id-ID', {
        maximumFractionDigits: Math.abs(rounded) >= 100 ? 0 : 2,
        minimumFractionDigits: 0,
      }).format(rounded)
    )
  }
  const rounded = Number.isInteger(n) ? n : Math.round(n * 100) / 100
  return String(rounded)
}

const BarChart: React.FC<Props> = ({
  className,
  color = 'primary',
  title = '',
  subtitle = '',
  height = '300px',
  series = [],
  categories = [],
  total = 0,
  isCurrency = true,
  isTotal = true,
  data = [],
  headers = [],
  formatCurrency = [],
  layout = 'vertical',
  isLoading = false,
  showTable = false,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartDomId = useId().replace(/:/g, '')
  const {mode} = useThemeMode()

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, series, categories, isCurrency, chartDomId])

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, color, series, categories, isCurrency)
    )
    if (chart) {
      chart.render()
    }

    return chart
  }

  const columnLayout = useMemo(
    () => clsx({col: layout === 'horizontal', 'col-12': layout === 'vertical'}),
    [layout]
  )

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{title}</span>

          {subtitle && <span className='text-muted fw-semibold fs-7'>{subtitle}</span>}
        </h3>
        {isTotal && (
          <div className='card-toolbar'>
            <span className={`card-title fw-bold fs-1 text-${color}`}>
              {isCurrency ? 'Rp. ' : ''} <FormattedNumber value={total} />
            </span>
          </div>
        )}
      </div>
      <div className='card-body'>
        <div className='row'>
          <div className={columnLayout}>
            {isLoading && <LoadingChart />}
            {data === null && <label className='form-label'>no result data</label>}
            {!isLoading && (
              <div ref={chartRef} id={`dashboard-bar-chart-${chartDomId}`} style={{height: height}} />
            )}
          </div>
          {showTable &&
            Array.isArray(data) &&
            data.length > 0 &&
            Array.isArray(headers) &&
            headers.length > 0 && (
              <div className={columnLayout}>
                <div className='table-responsive mt-5 px-5'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='min-w-30px'>No</th>
                        {headers.map((x) => (
                          <th className='min-w-30px' key={x}>
                            {x}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className='text-dark mb-1 fs-6'>
                      {data.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <span className='text-dark fw-semibold d-block fs-6'>{index + 1}</span>
                          </td>
                          {Object.keys(item).map((key) => {
                            const useFormatter = formatCurrency.includes(key)
                            const value = item[key] ?? 0
                            return (
                              <td key={`${index}-${key}`}>
                                <span className='text-dark fw-bold fs-6'>
                                  {useFormatter
                                    ? `Rp. ${new Intl.NumberFormat('id-ID', {
                                        maximumFractionDigits: 2,
                                      }).format(value)}`
                                    : item[key]}
                                </span>
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}

export {BarChart}

function getChartOptions(
  height: number,
  color: string,
  series: any,
  categories,
  isCurrency: boolean
): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue(`--bs-${color}`)
  const secondaryColor = getCSSVariableValue(`--bs-gray-300`)

  return {
    series: series,
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height: height,
      // width: series?.length * 1000,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '30%',
        borderRadius: 5,
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        formatter: function (val) {
          return formatChartAxisValue(val, isCurrency)
        },
      },
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return formatChartAxisValue(val, isCurrency)
        },
      },
    },
    colors: [baseColor, secondaryColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}
